import { ensureFreshAccessToken, supabase } from '../../shared/supabase/supabaseClient'
import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent'
import { messageFromInvoke, throwIfInvokeError } from '../../shared/supabase/invokeErrorUtils'
import type { InvokeErrorPayload } from '../../shared/supabase/invokeErrorUtils';
export type SaleQrResponse = {
  ok: boolean;
  qrData: string | null;
  qrUrl: string | null;
  orderId: string | null;
  mpPaymentId: string | null;
  mpOperationNumber?: string | null;
  mpOrderNumber?: string | null;
  paymentReconciliationStatus: string | null;
  status: string;
  salePaymentStatus?: string | null;
  salePaymentRowStatus?: string | null;
  amount?: number | null;
  expiresAt?: string | null;
  qrCreatedAt?: string | null;
  qrAttempt?: number | null;
  fiscalMode?: string | null;
  fiscalStatus?: string | null;
  saleVoided?: boolean;
  missingPayment?: boolean;
  qrPending?: boolean;
};

export type MpQrPaymentStatusRpc = {
  ok: boolean;
  sale_voided?: boolean;
  missing_payment?: boolean;
  sale_payment_status?: string | null;
  sale_payment_row_status?: string | null;
  payment_reconciliation_status?: string | null;
  qr_data?: string | null;
  qr_url?: string | null;
  qr_pending?: boolean;
  amount?: number | null;
  expires_at?: string | null;
  qr_created_at?: string | null;
  qr_attempt?: number | null;
  fiscal_mode?: string | null;
  fiscal_status?: string | null;
  error?: string;
};

const mpQrStatusInflight = new Map<string, Promise<MpQrPaymentStatusRpc>>();

async function authHeaders(): Promise<Record<string, string>> {
  const token = await ensureFreshAccessToken();
  if (!token) throw new Error('Sesión requerida. Iniciá sesión de nuevo.');
  return { Authorization: `Bearer ${token}` };
}

/** Monto del pago MP QR consultado al server (Edge Function get-sale-qr). */
export async function fetchMercadoPagoQrPaymentAmount(saleId: string): Promise<number | null> {
  try {
    const qr = await getSaleQr(saleId.trim());
    const amount = qr.amount;
    if (amount == null || !Number.isFinite(amount) || amount <= 0) return null;
    return amount;
  } catch {
    return null;
  }
}

/** Crea/actualiza el order QR en Mercado Pago para una venta ya sincronizada. */
export async function createMercadoPagoQrForSale(
  saleId: string,
  total: number,
  options?: { regenerate?: boolean }
): Promise<void> {
  const headers = await authHeaders();

  const { data, error, presented } = await invokeEdgeOrPresent<InvokeErrorPayload & { qrData?: string }>(
    'mercadopago-qr',
    {
      body: {
        saleId: saleId.trim(),
        total,
        regenerate: options?.regenerate === true,
      },
      headers,
    },
    {
      module: 'mercadopago',
      op: 'mp.qr_create',
      fallbackMessage: 'Error al generar QR en Mercado Pago',
    },
  );
  if (error) {
    if (presented) {
      throw error instanceof Error
        ? error
        : new Error(messageFromInvoke(data, error, 'Error al generar QR en Mercado Pago'));
    }
    throw new Error(messageFromInvoke(data, error, 'Error al generar QR en Mercado Pago'));
  }
  throwIfInvokeError(data);
}

/** RPC liviano — estado de cobro MP QR (fallback Realtime). */
export async function fetchMpQrPaymentStatus(saleId: string): Promise<MpQrPaymentStatusRpc> {
  const key = saleId.trim();
  const existing = mpQrStatusInflight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const { data, error } = await supabase.schema('pos').rpc('get_mp_qr_payment_status', {
      p_sale_id: key,
    });
    if (error) throw new Error(error.message || 'Error al consultar estado del pago');
    return (data ?? { ok: false }) as MpQrPaymentStatusRpc;
  })().finally(() => {
    if (mpQrStatusInflight.get(key) === promise) {
      mpQrStatusInflight.delete(key);
    }
  });

  mpQrStatusInflight.set(key, promise);
  return promise;
}

function emptyQrResponse(status: string, overrides?: Partial<SaleQrResponse>): SaleQrResponse {
  return {
    ok: false,
    qrData: null,
    qrUrl: null,
    orderId: null,
    mpPaymentId: null,
    paymentReconciliationStatus: null,
    status,
    ...overrides,
  };
}

function normalizeSaleQrResponse(
  input: Partial<SaleQrResponse> & { saleVoided?: boolean; missingPayment?: boolean; status?: string }
): SaleQrResponse {
  if (input.saleVoided) {
    return emptyQrResponse('voided', { saleVoided: true });
  }
  if (input.missingPayment) {
    return emptyQrResponse('pending', {
      missingPayment: true,
      paymentReconciliationStatus: input.paymentReconciliationStatus ?? null,
      salePaymentStatus: input.salePaymentStatus ?? null,
    });
  }
  return {
    ok: true,
    qrData: input.qrData ?? null,
    qrUrl: input.qrUrl ?? null,
    orderId: input.orderId ?? null,
    mpPaymentId: input.mpPaymentId ?? null,
    mpOperationNumber: input.mpOperationNumber ?? null,
    mpOrderNumber: input.mpOrderNumber ?? null,
    paymentReconciliationStatus: input.paymentReconciliationStatus ?? null,
    status: input.status ?? 'pending',
    salePaymentStatus: input.salePaymentStatus ?? null,
    salePaymentRowStatus: input.salePaymentRowStatus ?? null,
    amount: input.amount ?? null,
    expiresAt: input.expiresAt ?? null,
    qrCreatedAt: input.qrCreatedAt ?? null,
    qrAttempt: input.qrAttempt ?? null,
    fiscalMode: input.fiscalMode ?? null,
    fiscalStatus: input.fiscalStatus ?? null,
    qrPending: input.qrPending === true,
  };
}

/** Obtiene los datos del QR de Mercado Pago para una venta (tras el sync). */
export const getSaleQr = async (saleId: string): Promise<SaleQrResponse> => {
  const headers = await authHeaders();
  const { data, error, presented } = await invokeEdgeOrPresent('get-sale-qr', {
    body: { saleId: saleId.trim() },
    headers,
  }, {
    module: 'mercadopago',
    op: 'mp.get_sale_qr',
    fallbackMessage: 'Error al obtener QR',
  });
  if (error) {
    if (presented) {
      throw error instanceof Error ? error : new Error(error.message || 'Error al obtener QR');
    }
    throw new Error(error.message || 'Error al obtener QR');
  }
  const body = (data ?? {}) as SaleQrResponse & { reason?: string; error?: string };
  if (body?.error) throw new Error(body.error);
  return normalizeSaleQrResponse({
    ...body,
    saleVoided: body.reason === 'sale_voided',
    missingPayment: !body?.ok,
  });
};

/** Consulta get-sale-qr (sincroniza estado con MP si sigue pending). Best-effort. */
export async function syncMpQrPaymentStatusRemote(saleId: string): Promise<SaleQrResponse | null> {
  if (!saleId.trim()) return null;
  try {
    return await getSaleQr(saleId.trim());
  } catch {
    return null;
  }
}

export function rpcToSaleQrSnapshot(rpc: MpQrPaymentStatusRpc): SaleQrResponse {
  return normalizeSaleQrResponse({
    saleVoided: rpc.sale_voided,
    missingPayment: rpc.missing_payment,
    paymentReconciliationStatus: rpc.payment_reconciliation_status,
    salePaymentStatus: rpc.sale_payment_status,
    salePaymentRowStatus: rpc.sale_payment_row_status,
    qrData: rpc.qr_data,
    qrUrl: rpc.qr_url,
    amount: rpc.amount,
    expiresAt: rpc.expires_at,
    qrCreatedAt: rpc.qr_created_at,
    qrAttempt: rpc.qr_attempt,
    fiscalMode: rpc.fiscal_mode,
    fiscalStatus: rpc.fiscal_status,
    qrPending: rpc.qr_pending,
    status: rpc.sale_payment_row_status ?? 'pending',
  });
}
